import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BreachBadge, CxBadge, SeverityBadge, TicketStatusBadge } from '@/components/tickets/badges';
import { categoryUsesCx } from '@/types/domain';
import { formatDateTime, formatRelative } from '@/lib/format';
import { categoryName } from '@/lib/labels';
import type { ActionRequiredBy, TicketSummary, UserRef } from '@/types/api';

interface Props {
  tickets: TicketSummary[];
  showOwner?: boolean;
  emptyLabel?: string;
  /**
   * V4.9.2+ — Optional inline action button(s) per row. Used by the
   * Deleted + Archived pages so the "Restore" button lives RIGHT NEXT to
   * each ticket instead of being dumped in a chaotic grid below the table.
   */
  rowAction?: (ticket: TicketSummary) => React.ReactNode;
  /** Header label for the action column. Defaults to "Actions". */
  rowActionLabel?: string;
}

/**
 * V4.9 — All Tickets table.
 *
 * Column order (per ops spec):
 *   Ticket · Category · Severity · Status · SLA · CX ·
 *   Owner · Communicator · Solver · Created by · Created at
 *
 * Removed: Customer column (email is visible inside the ticket detail).
 */
export function TicketTable({ tickets, showOwner = true, emptyLabel, rowAction, rowActionLabel = 'Actions' }: Props) {
  // V4.9.3 — capture the list page the user is currently viewing so each
  // ticket-row link can hand that origin to the detail page. The detail
  // page uses it for the Back button + the sidebar uses it to keep the
  // right nav item highlighted (e.g. "My Queue" stays highlighted while
  // viewing a ticket the user opened from My Queue).
  //
  // V4.9.5+ BUGFIX — include `location.search` (the ?sort=created_at.desc&
  // page=2&status=Open query string) in the `from` state so the Back link
  // restores the EXACT filter state the user was viewing. Previously only
  // `pathname` was captured, so clicking back from a ticket detail dumped
  // the user on a clean /tickets (default Smart sort) and threw away every
  // filter, sort, and page they had chosen.
  const location = useLocation();
  const fromWithQuery = location.pathname + (location.search || '');
  if (tickets.length === 0) {
    return (
      <div className="state">
        <h4>No tickets</h4>
        <p className="text-small">{emptyLabel ?? 'No tickets match the current filters.'}</p>
      </div>
    );
  }
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Ticket</th>
            <th>Category</th>
            <th>Severity</th>
            <th>Status</th>
            {/* V4.9.2+ — "Action awaited by" — dynamic, shows whose turn it is. */}
            <th>Action awaited by</th>
            <th>Due</th>
            <th>CX</th>
            {showOwner ? <th>Owner</th> : null}
            <th>Communicator</th>
            <th>Solver</th>
            <th>Created by</th>
            <th>Created</th>
            {rowAction ? <th>{rowActionLabel}</th> : null}
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => (
            <tr key={ticket.id} className="no-hover" style={{ cursor: 'default' }}>
              <td>
                <Link
                  to={`/tickets/${ticket.id}`}
                  // V4.9.5 — Pass:
                  //   • `from`        — list page to return to (Back button),
                  //                     INCLUDING the ?sort=...&status=...
                  //                     query so the back nav restores the
                  //                     user's exact filter state. V4.9.5+
                  //                     fix.
                  //   • `ticketIds`   — full ordered list of visible ticket
                  //                     ids so the detail page's ← → arrows
                  //                     can step through them
                  state={{
                    from: fromWithQuery,
                    ticketIds: tickets.map((t) => t.id)
                  }}
                  className="font-mono"
                >
                  {ticket.id}
                </Link>
                <div className="text-small text-muted">
                  {ticket.order_number ? `Order ${ticket.order_number}` : 'No order'}
                </div>
              </td>
              <td>
                <span className="font-mono">{ticket.category}</span>
                <div className="text-small text-muted">
                  {categoryName(ticket.category) ?? '—'}
                </div>
                {/* V4.9.13 — show the B1 quotation sub-type so Quotation
                    tickets are distinguishable in the list, not identical. */}
                {ticket.quotation_type ? (
                  <div className="text-small" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                    {ticket.quotation_type}
                  </div>
                ) : null}
                <div className="text-small text-muted">{ticket.store_code}</div>
              </td>
              <td>
                <SeverityBadge severity={ticket.severity} />
              </td>
              <td>
                <TicketStatusBadge status={ticket.status} />
              </td>
              {/* V4.9.2+ — Action awaited by — color-coded by tone. */}
              <td>
                <ActionAwaitedCell action={ticket.action_required_by} />
              </td>
              <td>
                <BreachBadge
                  flag={ticket.breach_flag}
                  responsible={ticket.breach_responsible}
                  hoursOver={ticket.hrs_to_breach}
                />
                {ticket.hrs_to_breach !== null ? (
                  <div className="text-small text-muted">
                    {ticket.hrs_to_breach >= 0
                      ? `${ticket.hrs_to_breach.toFixed(1)}h left`
                      : `${Math.abs(ticket.hrs_to_breach).toFixed(1)}h over`}
                  </div>
                ) : null}
              </td>
              <td>
                {/* V4.9.13 — CX only applies to categories with a Log CX
                    stage; A1 + B-flow show "—" instead of misleading CX. */}
                {categoryUsesCx(ticket.category) ? (
                  <>
                    <CxBadge status={ticket.cx_status} />
                    {ticket.hrs_since_cx !== null && ticket.hrs_since_cx !== undefined ? (
                      <div className="text-small text-muted">
                        {ticket.hrs_since_cx.toFixed(1)}h since CX
                      </div>
                    ) : null}
                  </>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
              {showOwner ? (
                <td>
                  <PersonCell user={ticket.owner} />
                </td>
              ) : null}
              <td>
                <PersonCell user={ticket.communicator} />
              </td>
              <td>
                <PersonCell user={ticket.solver} />
              </td>
              <td>
                <PersonCell user={ticket.raised_by} />
              </td>
              <td>
                <div>{formatRelative(ticket.created_at)}</div>
                <div className="text-small text-muted">{formatDateTime(ticket.created_at)}</div>
              </td>
              {rowAction ? <td>{rowAction(ticket)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * V4.9 — Compact cell for a person reference. Shows name + @alias under it.
 * Renders a muted "Unassigned" placeholder when the role is empty.
 */
/**
 * V4.9.2+ — "Action awaited by" cell.
 * Red pill for active tickets (Solver/Approver/Communicator/Owner needs to act).
 * Amber for external-blocked (Customer/Design/Vendor).
 * Green ✓ for Closed.
 * Grey for Cancelled.
 *
 * Always shows the role label uppercase + name, so admins can see at a glance
 * which person is the bottleneck without reading the status column.
 */
function ActionAwaitedCell({ action }: { action: ActionRequiredBy }) {
  const roleLabel = (() => {
    switch (action.role) {
      case 'solver':       return 'SOLVER';
      case 'approver':     return 'APPROVER';
      case 'communicator': return 'COMMUNICATOR';
      case 'owner':        return 'OWNER';
      case 'customer':     return null;  // name already says "Waiting on Customer"
      case 'design':       return null;
      case 'vendor':       return null;
      case 'done':         return null;
      case 'cancelled':    return null;
    }
  })();
  const icon = action.tone === 'green' ? '✓ ' : action.tone === 'grey' ? '✗ ' : action.tone === 'amber' ? '⏸ ' : '';
  return (
    <div className={`action-awaited action-awaited-${action.tone}`}>
      {roleLabel ? <span className="action-awaited-role">{roleLabel}</span> : null}
      <span className="action-awaited-name">{icon}{action.name}</span>
    </div>
  );
}

function PersonCell({ user }: { user: UserRef | null | undefined }) {
  if (!user) return <span className="text-muted">Unassigned</span>;
  const showAlias = user.alias && user.alias.toLowerCase() !== user.name.toLowerCase();
  return (
    <>
      <div>{user.name}</div>
      {showAlias ? <div className="text-small text-muted">@{user.alias}</div> : null}
    </>
  );
}
